import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { challengeApi, errorMessage } from '../api';
import { Eyebrow, Panel, ProgressBar, StatusPill, formatDate, timeAgo, useTick } from '../ui';

const HOW_IT_WORKS = [
    { title: 'Start', text: 'Read the instructions' },
    { title: 'Fill in', text: 'Answers save automatically' },
    { title: 'Submit', text: 'Checklist must be complete' },
    { title: 'Mentor review', text: 'Approve or return with comments' },
    { title: 'Unlocked', text: 'Next stage opens' }
];

export default function PracticalChallenges() {
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    useTick();

    useEffect(() => {
        challengeApi.list()
            .then(setItems)
            .catch((err) => setError(errorMessage(err, 'Could not load practical challenges')));
    }, []);

    const stage = items && items[0] && items[0].stage_label;
    const last = items && items[items.length - 1];
    const showNextPlaceholder = items && items.length > 0 && items.length % 3 !== 0 && last.settings.unlock_next_on_approval;

    return (
        <div className="mx-auto w-full max-w-6xl space-y-6 pb-12">
            <header>
                {stage && <Eyebrow>{stage}</Eyebrow>}
                <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">Practical Challenges</h1>
                <p className="mt-1 text-sm text-ink-600">Complete each challenge, submit it, and your mentor will review it. Approval unlocks the next stage.</p>
            </header>

            <Panel>
                <h2 className="text-[15px] font-semibold text-ink-950">How a challenge works</h2>
                <ol className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
                    {HOW_IT_WORKS.map((step, i) => (
                        <li key={step.title} className="rounded-xl border border-border bg-ink-50/60 px-3.5 py-3">
                            <p className="font-mono text-[11px] text-brand-700">{String(i + 1).padStart(2, '0')}</p>
                            <p className="mt-1 text-sm font-semibold text-ink-950">{step.title}</p>
                            <p className="mt-0.5 text-xs text-ink-500">{step.text}</p>
                        </li>
                    ))}
                </ol>
            </Panel>

            {error && <Panel><p className="text-sm text-red-700">{error}</p></Panel>}

            {!items && !error && (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                    {[1, 2, 3].map((i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-ink-100" />)}
                </div>
            )}

            {items && items.length === 0 && (
                <Panel className="text-center text-sm text-ink-600">No practical challenges have been released yet.</Panel>
            )}

            {items && items.length > 0 && (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((item) => <ChallengeCard key={item.id} item={item} />)}
                    {showNextPlaceholder && (
                        <article className="flex min-h-[260px] flex-col rounded-2xl border border-border bg-white/60 p-5">
                            <div className="flex items-center justify-between gap-2">
                                <Eyebrow>Challenge {items.length + 1}</Eyebrow>
                                <StatusPill status="locked" />
                            </div>
                            <h2 className="mt-3 text-lg font-bold text-ink-400">Next challenge</h2>
                            <p className="mt-1.5 text-sm text-ink-500">Unlocks when your mentor approves Challenge {items.length} and the next challenge is released.</p>
                            <div className="mt-auto pt-5">
                                <button type="button" disabled className="rounded-lg bg-ink-100 px-4 py-2 text-sm font-semibold text-ink-500">Locked</button>
                            </div>
                        </article>
                    )}
                </div>
            )}
        </div>
    );
}

function ChallengeCard({ item }) {
    const status = item.locked ? 'locked' : item.status;
    const active = !item.locked && ['in_progress', 'changes_requested'].includes(item.status);
    const workbook = item.layout === 'workbook';
    const href = `/practical-challenges/${item.id}`;

    const action = (() => {
        if (item.locked) return { label: 'Locked', disabled: true };
        switch (item.status) {
            case 'not_started': return { label: 'Start challenge', primary: true };
            case 'in_progress': return { label: workbook ? 'Continue workbook' : 'Continue', primary: true };
            case 'changes_requested': return { label: 'Make changes', primary: true };
            default: return { label: 'View submission' };
        }
    })();

    const statusLabel = item.status === 'in_progress' ? 'In progress' : undefined;

    return (
        <article className={`flex min-h-[260px] flex-col rounded-2xl border bg-white p-5 ${active ? 'border-brand-600 ring-1 ring-brand-600/40' : 'border-border'} ${item.locked ? 'opacity-70' : ''}`}>
            <div className="flex items-center justify-between gap-2">
                <Eyebrow>Challenge {item.number}</Eyebrow>
                <StatusPill status={status} label={statusLabel} />
            </div>
            <h2 className={`mt-3 text-lg font-bold ${item.locked ? 'text-ink-500' : 'text-ink-950'}`}>{item.title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{item.locked ? item.locked_reason : item.summary}</p>

            {!item.locked && (
                <div className="mt-3 space-y-2 text-xs text-ink-500">
                    {item.highlights.length > 0 && (
                        <p className="flex flex-wrap gap-x-5 gap-y-1">{item.highlights.map((h) => <span key={h}>{h}</span>)}</p>
                    )}
                    {workbook && ['in_progress', 'changes_requested'].includes(item.status) && (
                        <div className="pt-1">
                            <div className="mb-1.5 flex items-center justify-between text-xs">
                                <span className="text-ink-700">
                                    {item.progress.current_section
                                        ? `Part ${item.progress.current_section} of ${item.progress.total_sections} parts`
                                        : `${item.progress.done_count} of ${item.progress.total_sections} parts done`}
                                </span>
                                <span className="font-semibold text-ink-900">{item.progress.percent}%</span>
                            </div>
                            <ProgressBar percent={item.progress.percent} />
                        </div>
                    )}
                    {!workbook && item.status === 'in_progress' && (
                        <p>{item.progress.percent}% of required answers complete</p>
                    )}
                    {(item.meta_label || item.settings.due_date) && (
                        <p className="flex flex-wrap gap-x-5 gap-y-1">
                            {item.meta_label && <span>{item.meta_label}</span>}
                            {item.settings.due_date && <span>Due: {formatDate(item.settings.due_date)}</span>}
                        </p>
                    )}
                    {item.submitted_at && ['submitted', 'approved'].includes(item.status) && <p>Submitted: {formatDate(item.submitted_at)}</p>}
                    {item.status === 'approved' && item.score && <p>Readiness score: {item.score.total} / {item.score.max}</p>}
                    {['in_progress', 'changes_requested'].includes(item.status) && item.last_saved_at && <p>Last saved {timeAgo(item.last_saved_at)}</p>}
                </div>
            )}

            <div className="mt-auto pt-5">
                {action.disabled ? (
                    <button type="button" disabled className="inline-flex items-center gap-1.5 rounded-lg bg-ink-100 px-4 py-2 text-sm font-semibold text-ink-500">
                        <LockClosedIcon className="h-4 w-4" /> Locked
                    </button>
                ) : (
                    <Link to={href} className={action.primary ? 'btn-primary inline-flex' : 'btn-secondary inline-flex'}>{action.label}</Link>
                )}
            </div>
        </article>
    );
}
